import { useEffect } from 'react';
import { unstable_serialize, type Key, type Middleware } from 'swr';

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const MAX_TRACKED_KEYS = 500;

const fetchedAt = new Map<string, number>();

/**
 * Records when a key was fetched, evicting the oldest entries once the map exceeds its cap.
 * @param {string} cacheKey - Serialized SWR key.
 * @param {number} timestamp - Fetch time in milliseconds.
 */
const recordFetch = (cacheKey: string, timestamp: number) => {
  fetchedAt.delete(cacheKey);
  fetchedAt.set(cacheKey, timestamp);
  if (fetchedAt.size <= MAX_TRACKED_KEYS) return;
  const oldest = fetchedAt.keys().next().value;
  if (oldest !== undefined) fetchedAt.delete(oldest);
};

/**
 * Resolves a SWR key (or the first page key of an infinite key getter) to a stable string.
 * @param {Key} key - SWR key, or a getter function for infinite loading.
 * @returns Serialized key, or undefined when the key is disabled.
 */
const toCacheKey = (key: Key): string | undefined => {
  const resolved = typeof key === 'function' ? (key as (...a: Array<any>) => Key)(0, null) : key;
  return resolved ? unstable_serialize(resolved) : undefined;
};

/**
 * Creates a SWR middleware that revalidates cached data once it is older than the TTL.
 * Cached data younger than the TTL is served as-is; older data is revalidated when the hook
 * mounts or its key changes. Works with `useSWRImmutable`, which forces `revalidateIfStale` off.
 * @param {number} ttl - Time in milliseconds before cached data is considered stale.
 * @returns SWR middleware.
 */
export const swrTtlMiddleware =
  (ttl: number = ONE_DAY_MS): Middleware =>
  (useSWRNext) =>
  (key, fetcher, config) => {
    const cacheKey = toCacheKey(key);

    const fetcherWithTimestamp = fetcher
      ? async (...args: Array<any>) => {
          const result = await (fetcher as (...a: Array<any>) => any)(...args);
          if (cacheKey) recordFetch(cacheKey, Date.now());
          return result;
        }
      : fetcher;

    const swr = useSWRNext(key, fetcherWithTimestamp, config);
    const { data, mutate } = swr;

    useEffect(() => {
      if (!cacheKey || data === undefined) return;
      const timestamp = fetchedAt.get(cacheKey);
      if (timestamp !== undefined && Date.now() - timestamp > ttl) mutate();
    }, [cacheKey, data, mutate]);

    return swr;
  };
